import { Component, OnDestroy, OnInit, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { GlobalSearchService } from '../../services/globalsearchservice.service';
import { OmsService } from '../../services/oms.service';
import { WarehouseService } from '../warehouse.service';

/**
 * Warehouse pick board — the wall display.
 *
 * Read-only view of the same pickingfreeze work the pick station and the legacy
 * order-pick screen run on. It answers three questions from across the room:
 * what is next, how long until the truck leaves, and is anything late or short.
 * Refreshes on a timer and on the socket's orderitempick push, so a line picked
 * on an iPad moves on the wall a second later.
 */
@Component({
	selector: 'app-pick-board',
	templateUrl: './pick-board.component.html',
	styleUrls: ['./pick-board.component.scss'],
})
export class PickBoardComponent implements OnInit, OnDestroy {

	@ViewChild('boardRoot') boardRoot: ElementRef;

	user: any = false;
	location = '';
	locationName = '';

	loading = true;
	error = '';

	orders: any[] = [];
	ready: any[] = [];
	cutoffs: any[] = [];
	stats: any = {
		orders: 0, to_pick: 0, in_progress: 0, ready: 0,
		short: 0, late: 0, due_today: 0, lines: 0, lines_done: 0,
		units: 0, units_picked: 0, held: 0, pickers: 0,
	};

	now = new Date();
	lastUpdated: Date = null;
	live = false;
	fullscreen = false;

	// The board pages itself so row 14 gets its turn on the wall.
	page = 0;
	readonly pageSize = 7;
	readonly refreshMs = 20000;
	readonly pageMs = 14000;

	private clockTimer: any = null;
	private refreshTimer: any = null;
	private pageTimer: any = null;
	private pushTimer: any = null;
	private userSub: any = null;
	private inflight: any = null;

	constructor(
		public router: Router,
		private warehouseService: WarehouseService,
		private omsService: OmsService,
		private globalSearchService: GlobalSearchService,
	) {}

	ngOnInit(): void {
		this.userSub = this.globalSearchService.user.subscribe((u: any) => {
			if (!u) { return; }
			this.user = u;
			const loc = u.user && u.user.defaultlocation ? u.user.defaultlocation.loccode : '';
			if (loc && loc !== this.location) {
				this.location = loc;
			}
			this.connect();
			this.load();
		});

		this.clockTimer = setInterval(() => { this.now = new Date(); }, 1000);
		this.refreshTimer = setInterval(() => this.load(true), this.refreshMs);
		this.pageTimer = setInterval(() => this.nextPage(), this.pageMs);

		document.addEventListener('fullscreenchange', this.onFsChange);
	}

	ngOnDestroy(): void {
		[this.clockTimer, this.refreshTimer, this.pageTimer, this.pushTimer].forEach(t => {
			if (t) { clearInterval(t); clearTimeout(t); }
		});
		if (this.userSub) { this.userSub.unsubscribe(); }
		if (this.inflight) { this.inflight.unsubscribe(); }
		document.removeEventListener('fullscreenchange', this.onFsChange);
	}

	// ── Data ─────────────────────────────────────────────────────────────────

	load(silent = false) {
		if (!silent) { this.loading = true; }
		if (this.inflight) { this.inflight.unsubscribe(); }

		this.inflight = this.warehouseService.getPickBoard(this.location).subscribe({
			next: (r: any) => {
				this.loading = false;
				this.inflight = null;
				if (!r || r.success !== true) {
					this.error = (r && r.message) ? r.message : 'Pick board did not load.';
					return;
				}
				this.error = '';
				this.locationName = r.location_name || '';
				this.cutoffs = r.cutoffs || [];
				this.stats = r.stats || this.stats;
				const all = r.orders || [];
				this.orders = all.filter((o: any) => o.bucket !== 'ready');
				this.ready = all.filter((o: any) => o.bucket === 'ready');
				this.lastUpdated = new Date();
				if (this.page > this.pageCount - 1) { this.page = 0; }
			},
			error: () => {
				this.loading = false;
				this.inflight = null;
				this.error = 'Pick board is not reachable.';
			},
		});
	}

	/**
	 * The socket fires once per scanned line; a busy floor would otherwise
	 * hammer the rollup query. Coalesce into one reload a second later.
	 */
	private connect() {
		const token = this.user && this.user.user ? this.user.user.userid : '';
		if (!token) { return; }

		this.omsService.setupSocketConnection(token);
		this.live = true;

		this.omsService.subscribeToOrderPicks((_err: any, _msg: any) => {
			if (this.pushTimer) { clearTimeout(this.pushTimer); }
			this.pushTimer = setTimeout(() => this.load(true), 1200);
		});
	}

	// ── Paging ───────────────────────────────────────────────────────────────

	get pageCount(): number {
		return Math.max(1, Math.ceil(this.orders.length / this.pageSize));
	}

	get pagedOrders(): any[] {
		const start = this.page * this.pageSize;
		return this.orders.slice(start, start + this.pageSize);
	}

	nextPage() {
		if (this.pageCount <= 1) { this.page = 0; return; }
		this.page = (this.page + 1) % this.pageCount;
	}

	rankOf(index: number): number {
		return this.page * this.pageSize + index + 1;
	}

	// ── Cutoffs & clock ──────────────────────────────────────────────────────

	/** The next deadline that hasn't passed — the one the countdown shows. */
	get nextCutoff(): any {
		for (const c of this.cutoffs) {
			if (this.minutesLeft(c) > 0) { return c; }
		}
		return null;
	}

	/** Recomputed from the wall clock so the countdown ticks between refreshes. */
	minutesLeft(cutoff: any): number {
		if (!cutoff || !cutoff.at) { return 0; }
		const at = new Date(cutoff.at).getTime();
		return Math.floor((at - this.now.getTime()) / 60000);
	}

	countdown(cutoff: any): string {
		const m = this.minutesLeft(cutoff);
		if (m <= 0) { return 'CLOSED'; }
		const h = Math.floor(m / 60);
		const mm = m % 60;
		return h > 0 ? `${h}h ${String(mm).padStart(2, '0')}m` : `${mm}m`;
	}

	cutoffTone(cutoff: any): string {
		const m = this.minutesLeft(cutoff);
		if (m <= 0) { return 'closed'; }
		if (m <= 30) { return 'critical'; }
		if (m <= 90) { return 'warn'; }
		return 'ok';
	}

	/** Per-order urgency, which is what actually colours the row. */
	rowTone(o: any): string {
		if (o.late) { return 'critical'; }
		if (o.short_lines > 0) { return 'short'; }
		const m = o.minutes_to_cutoff;
		if (m !== null && m !== undefined) {
			const live = this.minutesFromNow(o);
			if (live <= 0) { return 'closed'; }
			if (live <= 30) { return 'critical'; }
			if (live <= 90) { return 'warn'; }
		}
		if (o.asap) { return 'warn'; }
		return 'ok';
	}

	minutesFromNow(o: any): number {
		const idx = o.cutoff_index;
		if (idx === null || idx === undefined || !this.cutoffs[idx]) { return 9999; }
		return this.minutesLeft(this.cutoffs[idx]);
	}

	orderCountdown(o: any): string {
		const idx = o.cutoff_index;
		if (idx === null || idx === undefined || !this.cutoffs[idx]) { return '—'; }
		return this.countdown(this.cutoffs[idx]);
	}

	age(o: any): string {
		const m = o.age_minutes || 0;
		if (m < 60) { return `${m}m`; }
		const h = Math.floor(m / 60);
		if (h < 48) { return `${h}h`; }
		return `${Math.floor(h / 24)}d`;
	}

	// ── Chrome ───────────────────────────────────────────────────────────────

	private onFsChange = () => {
		this.fullscreen = !!document.fullscreenElement;
	};

	toggleFullscreen() {
		const el = this.boardRoot ? this.boardRoot.nativeElement : null;
		if (!el) { return; }
		if (document.fullscreenElement) {
			document.exitFullscreen();
		} else if (el.requestFullscreen) {
			el.requestFullscreen();
		}
	}

	openOrder(o: any) {
		this.router.navigate(['/warehouse/pick-station', o.orderno]);
	}

	trackByOrderno(_i: number, o: any) {
		return o.orderno;
	}
}
